<template>
  <li
    id="self-birth-certificate"
    v-if="form.certificateOrderProduct.includes('birthCertificate')"
  >
    <span class="h3">Your birth certificate details</span>
    <div class="status warn">
      These are the details that are on your birth certificate.
    </div>
    <ol class="questions">
      <li>
        <fieldset>
          <legend>
            <span class="label">Place of birth or adoption</span>
          </legend>
          <ol class="questions">
            <li>
              <InputWithError 
                  v-model="form.birthTownCitySelf"
                  id="birthTownCitySelf"
                  name="Applicant Birth Town"
                  label="Town or city"
                  maxlength="60"
                  size="15"
                />
            </li>
            <li>
              <InputWithError 
                  v-model="form.birthStateSelf"
                  id="birthStateSelf"
                  name="Applicant Birth State"
                  label="State or territory"
                  maxlength="70"
                  size="15"
                />
            </li>
            <li>
              <InputWithError 
                  v-model="form.dateOfBirthSelf"
                  id="dateOfBirthSelf"
                  name="Applicant Date of Birth"
                  label="Date of birth"
                  maxlength="25"
                  size="10"
                  hint="dd/mm/yyy"
                  type="date"
                  :max="today"
                />
            </li>
            <li>
              Your registered name
              <InputWithError 
                v-model="form.selfGivenName"
                id="selfGivenName"
                name="Applicant Given Name"
                label="First Name"
                maxlength="60"
                size="35"
              />
            </li>
            <li>
              <label for="selfMiddleName">
                <span class="label">Middle name(s)</span></label
              >
              <input
                id="selfMiddleName"
                autocomplete="additional-name"
                v-model="form.selfMiddleNames"
                name="Self Middle Name(s)"
                type="text"
                class="stretchWidth"
                maxlength="255"
              />
            </li>
            <li>
              <InputWithError 
                v-model="form.selfFamilyName"
                id="selfFamilyName"
                name="Applicant Family Name"
                label="Family name"
                maxlength="60"
                size="30"
                hint="If you don't have a family name and only use the name above,
                enter n/a."
              />
            </li>
            <li>
              <ValidationProvider rules="required:#label-selfSex">
                <!-- use a hidden input to trick vue validate to work with select -->
                <input 
                  type="text" 
                  class="form-control disabled" 
                  name="Sex" 
                  v-model="form.selfSex" 
                  style="display:none;"
                />
              </ValidationProvider>
              <label :for="selfSex" id="label-selfSex">
                <span class="label">Sex</span>
                <abbr class="required" title="(required)">*</abbr>
                <small class="hint">This is the sex shown on your current certificate.</small>
              </label>
              <select
                id="selfSex"
                name="Sex"
                v-model="uiSex"
                @change="syncSelects(); onInputChange();"
              >
                <option selected disabled>- please select -</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
                <option value="anotherterm">Another Term</option>
              </select>
            </li>
            <li v-if="uiSex == 'anotherterm'">
              <InputWithError 
                v-model="form.selfSexOther"
                id="selfSexOther"
                name="Sex Descriptor"
                label="What descriptor is shown on your certificate?"
                maxlength="60"
                size="30"
              />
            </li>
          </ol>
        </fieldset>
      </li>
      <li>
        <fieldset>
          <legend>
            <span class="h4">Your mother or registered parent's details</span>
          </legend>
        </fieldset>
        <div class="status warn" id="mothers-details-instructions">
          These are your mother or parent's details that are on your birth
          certificate.
        </div>
        <ol class="questions">
          <li>
            <InputWithError 
              v-model="form.selfMothersGivenName"
              id="selfMothersGivenName"
              name="Mother's Given Name"
              label="First name"
              maxlength="60"
              size="35"
            />
          </li>
          <li>
            <label for="selfMothersMiddleNames">
              <span class="label">Middle name(s)</span></label
            >
            <input
              id="selfMothersMiddleNames"
              autocomplete="additional-name"
              v-model="form.selfMothersMiddleNames"
              name="Mother Middle Name(s)"
              type="text"
              class="stretchWidth"
              maxlength="255"
            />
          </li>
          <li>
            <InputWithError 
              v-model="form.selfMothersFamilyName"
              id="selfMothersFamilyName"
              name="Mother's Family Name"
              label="Family name"
              maxlength="60"
              size="30"
              hint="If your mother or parent doesn't have a family name and only uses the
              name above enter n/a."
            />
          </li>
          <li>
            <InputWithError 
              v-model="form.selfMothersBirthFamilyName"
              id="selfMothersBirthFamilyName"
              name="Birth Family Name"
              label="Birth family name"
              maxlength="60"
              size="30"
              hint="If their birth family name is the same as above, enter it again
              or if they didn't have a birth family name enter n/a."
            />
          </li>
        </ol>
        <fieldset>
          <legend>
            <span class="h4">Your father or registered parent's details</span>
          </legend>
          <div class="status warn" id="parents-details-instructions">
            These are your father or parent's details that are on your birth
            certificate.
          </div>
          <fieldset>
            <ol class="questions">
              <li>
                <InputWithError 
                  v-model="form.selfParentsGivenName"
                  id="selfParentsGivenName"
                  name="Parent's Family Name"
                  label="First name"
                  maxlength="60"
                  size="30"
                />
              </li>
              <li>
                <label for="selfParentsMiddleNames">
                  <span class="label">Middle name(s)</span></label
                >
                <input
                  id="selfParentsMiddleNames"
                  autocomplete="additional-name"
                  v-model="form.selfParentsMiddleNames"
                  name="Parent Middle Name(s)"
                  type="text"
                  class="stretchWidth"
                  maxlength="255"
                />
              </li>
              <li>
                <InputWithError 
                  v-model="form.selfParentsFamilyName"
                  id="selfParentsFamilyName"
                  name="Parent's Family Name"
                  label="Family name"
                  maxlength="60"
                  size="30"
                  hint="If your father or parent doesn't have a family name and only
                  uses the name above, enter n/a."
                />
              </li>
              <li>
            <InputWithError 
              v-model="form.selfParentsBirthFamilyName"
              id="selfParentsBirthFamilyName"
              name="Birth Family Name"
              label="Birth Family name"
              maxlength="60"
              size="30"
              hint="If their birth family name is the same as above, enter it again
              or if they didn't have a birth family name enter n/a."
            />
          </li>
            </ol>
          </fieldset>
        </fieldset>
      </li>
    </ol>
  </li>
</template>

<script>
import InputWithError from '../../InputWithError.vue';

export default {
  components: {
    InputWithError
  },
  methods: {
    syncSelects() {
      this.form.selfSex = this.uiSex;
    }
  },
  data() {
    return {
      uiSex: ""
    }
  },
  props: {
    value: Object,
    today: String,
  },
  computed: {
    form: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
  },
};
</script>
